import "./newSideBar.scss"
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TieredMenu } from 'primereact/tieredmenu';
import { Tooltip } from "primereact/tooltip";
import dashlyIcon from '../../Images/logonew.png';
import { readCachedCustomerLogo, storedUserCustomerUuid } from "../custom-styling/customerLogoCache";
import { prefetchRouteFromEvent } from "../../routes/routePreload";
import { CustomerService } from "../../Services/customerService";
import { UserService } from "../../Services/userService";
import { getStoredPermissions } from "../../Util/secureStorageSession";
import {
	items,
	aiItem,
	accessItem,
	systemValueItems,
	getActiveMenusFromPath,
	dashboards,
    alertsItem,
    projectsItem,
    marketplaceItem,
    processflowItem,
    objectiveItem,
    knowledgeBaseItem,
    settingsItem,
    dataHeroItem
} from "./SideBarItems/SideBarItems";

interface toggleSideBarProps {
    toggleSystemValue: () => void;
    sideBar: boolean;
    systemValueToggle: boolean;
    closeDialog: (open: boolean) => void;
}

const NewSideBar = ({ sideBar, closeDialog, toggleSystemValue, systemValueToggle }: toggleSideBarProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const permissions = getStoredPermissions();
    const [activeParent, setActiveParent] = useState<string | null>(null);
    const [activeChild, setActiveChild] = useState<string | null>(null);
    // Start from the cached customer logo (warmed at sign-in) so the first paint is
    // already branded; the effect below only refreshes it when nothing is cached.
    const [customerLogo, setCustomerLogo] = useState<string>(
        () => readCachedCustomerLogo(storedUserCustomerUuid()) ?? dashlyIcon,
    );
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 1201) {
                setIsMobile(true);
            } else {
                setIsMobile(false)
            }
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);


    useEffect(() => {
        const { parent, child } = getActiveMenusFromPath(location.pathname);
        setActiveParent(parent);
        setActiveChild(child);
    }, [location.pathname]);

    // Close sidebar on mobile when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (isMobile && sideBar) {
                const sidebar = document.querySelector('.newSidebar');
                if (sidebar && !sidebar.contains(event.target as Node)) {
                    closeDialog(false);
                }
            }
        };

        if (isMobile && sideBar) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMobile, sideBar, closeDialog]);

    // Add/remove sidebar-open class for mobile
    useEffect(() => {
        if (isMobile) {
            if (sideBar) {
                document.body.classList.add('sidebar-open');
            } else {
                document.body.classList.remove('sidebar-open');
            }
        } else {
            document.body.classList.remove('sidebar-open');
        }

        return () => {
            document.body.classList.remove('sidebar-open');
        };
    }, [isMobile, sideBar]);


    const systemValueItemsPermissionsPresent = () => {
        return permissions.includes("Dataservice.View") || permissions.includes("Cache.View") || permissions.includes("DashlyEmail.View") || permissions.includes("GenericSelectItem.View") || permissions.includes("Graph_Type.View") || permissions.includes("Metric.View") || permissions.includes("MetricView.View") || permissions.includes("Component.View") || permissions.includes("Permission.View") || permissions.includes("DashboardCategory.View");
    }

    const markSubmenuOpen = (open: boolean) => {
        document.querySelector('.newSidebar')?.classList.toggle('newSidebar--submenu-open', open);
    };

    const closeOpenSubmenus = () => {
        document.querySelectorAll('.newSidebar .p-submenu-list').forEach((submenu) => {
            (submenu as HTMLElement).style.display = 'none';
        });
        markSubmenuOpen(false);
    };

    const positionSubmenu = (submenu: HTMLElement, item: HTMLElement) => {
        const rect = item.getBoundingClientRect();
        submenu.style.display = 'block';
        submenu.style.position = 'fixed';
        submenu.style.left = `${rect.right}px`;
        submenu.style.right = 'auto';
        submenu.style.minWidth = 'max-content';
        submenu.style.width = 'max-content';
        submenu.style.zIndex = '200';
        const height = submenu.getBoundingClientRect().height;
        const maxTop = Math.max(8, window.innerHeight - height - 8);
        submenu.style.top = `${Math.min(rect.top, maxTop)}px`;
        markSubmenuOpen(true);
    };

    const systemRootItem = () =>
        document.querySelector('.newSidebar .menu.system .p-tieredmenu-root-list > .p-menuitem') as HTMLElement | null;

    const showSystemSubmenu = () => {
        const item = systemRootItem();
        const submenu = item?.querySelector('.p-submenu-list') as HTMLElement | null;
        if (item && submenu) {
            positionSubmenu(submenu, item);
        }
    };

    const onHover = {
        menuitem: {
            onMouseEnter: (event: { currentTarget: HTMLElement }) => {
                const item = event.currentTarget;
                const submenu = item.querySelector('.p-submenu-list') as HTMLElement | null;
                if (submenu) {
                    positionSubmenu(submenu, item);
                }
            },
            onMouseLeave: (event: { currentTarget: HTMLElement }) => {
                const submenu = event.currentTarget.querySelector('.p-submenu-list') as HTMLElement | null;
                if (submenu) {
                    submenu.style.display = 'none';
                    markSubmenuOpen(false);
                }
            }
        }
    };

    useEffect(() => {
        if (!systemValueToggle) {
            closeOpenSubmenus();
            return;
        }
        const frame = window.requestAnimationFrame(() => {
            showSystemSubmenu();
        });
        return () => window.cancelAnimationFrame(frame);
    }, [systemValueToggle]);

    useEffect(() => {
        if (!sideBar) {
            closeOpenSubmenus();
        }
    }, [sideBar]);

    useEffect(() => {
        const fetchCustomerLogo = async () => {
            try {
                // The stored user carries `customerUuid` (not `customer.uuid`, which this
                // fast path used to check — so it never hit and always went via /me).
                const customerUuid = storedUserCustomerUuid();
                if (customerUuid) {
                    const cached = readCachedCustomerLogo(customerUuid);
                    if (cached) {
                        setCustomerLogo(cached);
                        return;
                    }
                    await fetchCustomerLogoFromAPI(customerUuid);
                    return;
                }

                await fetchCustomerLogoFromAPI();
            } catch (error) {
            }
        };

        const fetchCustomerLogoFromAPI = async (knownCustomerUuid?: string) => {
            try {
                const customerService = new CustomerService();
                let customerUuid = knownCustomerUuid;

                if (!customerUuid) {
                    const userService = new UserService();
                    const userResponse = await userService.retrieveMe();
                    if (!userResponse?.data?.data?.customer?.uuid) return;
                    customerUuid = userResponse.data.data.customer.uuid;
                }

                const storedLogos = JSON.parse(localStorage.getItem('customerLogos') || '{}');
                if (storedLogos[customerUuid]) {
                    setCustomerLogo(storedLogos[customerUuid]);
                    return;
                }

                const logoResponse = await customerService.getCustomerLogo(customerUuid);

                if (logoResponse?.data?.data && logoResponse.data.data.logo) {
                    const logo = logoResponse.data.data.logo;
                    setCustomerLogo(logo);

                    storedLogos[customerUuid] = logo;
                    localStorage.setItem('customerLogos', JSON.stringify(storedLogos));
                } else {
                    setCustomerLogo(dashlyIcon);
                }
            } catch (error) {
                setCustomerLogo(dashlyIcon);
            }
        };
        fetchCustomerLogo();
    }, []);


    return (
            <div className="newSidebar" style={{
                display: `${isMobile && !sideBar ? 'none' : 'flex'}`
            }}
                // Prefetch the route chunk of the hovered/focused menu item (see routes/routePreload).
                onMouseOver={prefetchRouteFromEvent}
                onFocusCapture={prefetchRouteFromEvent}>

            <div className="icon">
                <a className="link flex align-items-center" onClick={() => navigate("/")}>
                    <img
                        src={customerLogo}
                        alt="icon"
                        className="dashly_icon"
                    />
                </a>
            </div>

            {!isMobile && (
                <>
                    <Tooltip target=".menu.top-tooltip:not(.management)" showDelay={100} hideDelay={100} position="top" />
                    <Tooltip target=".menu:not(.top-tooltip):not(.system)" showDelay={100} hideDelay={100} position="right" />
                </>
            )}

            <div className="newSidebar-scroll" onScroll={closeOpenSubmenus}>
            <TieredMenu data-pr-tooltip={t("sidebar.home")} model={items(navigate, isMobile, closeDialog, activeParent)}
                breakpoint="90px" className={`menu ${activeParent === 'items' ? 'active' : ''}`} />

            <TieredMenu data-pr-tooltip={t("sidebar.ai")} model={aiItem(navigate, isMobile, closeDialog, permissions, activeParent)}
                breakpoint="90px" className={`menu ${activeParent === 'aiItem' ? 'active' : ''}`} />

            <TieredMenu data-pr-tooltip={t("sidebar.dashboards")} model={dashboards(navigate, isMobile, closeDialog, permissions, activeParent)}
                breakpoint="90px" className={`menu ${activeParent === 'dashboards' ? 'active' : ''}`} />

            <TieredMenu data-pr-tooltip="Alerts" model={alertsItem(navigate, isMobile, closeDialog, permissions, activeParent)}
                breakpoint="90px" className={`menu ${activeParent === 'alertsItem' ? 'active' : ''}`} />

            <TieredMenu data-pr-tooltip="Processflows" model={processflowItem(navigate, isMobile, closeDialog, permissions, activeParent)}
                breakpoint="90px" className={`menu ${activeParent === 'processflowItem' ? 'active' : ''}`} />

            <TieredMenu data-pr-tooltip={t("objective.list.title")} model={objectiveItem(navigate, isMobile, closeDialog, permissions, activeParent)}
                breakpoint="90px" className={`menu ${activeParent === 'objectiveItem' ? 'active' : ''}`} />

            <TieredMenu data-pr-tooltip={t("knowledgeBase.title")} model={knowledgeBaseItem(navigate, isMobile, closeDialog, permissions, activeParent)}
                breakpoint="90px" className={`menu ${activeParent === 'knowledgeBaseItem' ? 'active' : ''}`} />

            {systemValueItemsPermissionsPresent() &&
                <TieredMenu data-pr-tooltip={t("sidebar.system-values")} model={systemValueItems(navigate, isMobile, closeDialog, permissions, t, activeParent, activeChild, systemValueToggle, toggleSystemValue)}
                    breakpoint="90px" className="menu system top-tooltip" {...(!isMobile ? { pt: onHover } : {})} />}

            <TieredMenu data-pr-tooltip={t("project.list.title")} model={projectsItem(navigate, isMobile, closeDialog, permissions, activeParent)}
                        breakpoint="90px" className={`menu ${activeParent === 'projectsItem' ? 'active' : ''}`} />

            <TieredMenu data-pr-tooltip={t("marketplace")} model={marketplaceItem(navigate, isMobile, closeDialog, permissions, activeParent)}
                        breakpoint="90px" className={`menu ${activeParent === 'marketplaceItem' ? 'active' : ''}`} />

            <TieredMenu data-pr-tooltip={t("sidebar.data-hero")} model={dataHeroItem(navigate, isMobile, closeDialog, permissions, activeParent)}
                        breakpoint="90px" className={`menu ${activeParent === 'dataHeroItem' ? 'active' : ''}`} />

	            <div className="sidebar-bottom-actions">
                <div className="sidebar-divider" />
                <TieredMenu data-pr-tooltip={t("access-center.title")} model={accessItem(navigate, isMobile, closeDialog, permissions, activeParent)}
                    breakpoint="90px" className={`menu menu-bottom ${activeParent === 'accessItem' ? 'active' : ''}`} />
                <TieredMenu data-pr-tooltip={t("navbar.settings")} model={settingsItem(navigate, isMobile, closeDialog, activeParent)}
                    breakpoint="90px" className={`menu menu-bottom ${activeParent === 'settingsItem' ? 'active' : ''}`} />
            </div>
            </div>
            </div>
    )

}


export default NewSideBar;